import { useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import styled from "styled-components";
import { BASIC_DARK_GRAY, BASIC_WHITE } from "../../../constants/colors";
import { appActions } from "../../../ducks/app";
import { Menu } from "../../../ducks/menu";
import { Button } from "../../atoms/Button";
import { MenuCountSelect } from "../../molecules/menu/MenuCountSelect";
import { MenuImage } from "../../molecules/menu/MenuImage";
import { MenuPrice } from "../../molecules/menu/MenuPrice";

type Props = {
  menu: Menu;
  selectedLimitedMenus: Menu[];
  onFavorite: (
    menuCode: string,
    favorite: boolean,
    onSuccess?: () => void,
  ) => void;
  onSelectMenu: (
    amount: number,
    optionId?: number,
    optionName?: string,
  ) => void;
  noPopup?: boolean;
  disableClick?: boolean;
  showQuantitySelector?: boolean;
  quantity?: number;
  onQuantityChange?: (quantity: number) => void;
  onAddToCart?: () => void;
  shouldReset?: boolean;
};

export const MenuItemRecommendation: React.FC<Props> = ({
  menu,
  selectedLimitedMenus,
  onFavorite,
  onSelectMenu,
  noPopup,
  disableClick,
  showQuantitySelector = false,
  quantity = 0,
  onQuantityChange,
  onAddToCart,
  shouldReset = false,
}) => {
  const dispatch = useDispatch();

  const [isMenuClicked, setIsMenuClicked] = useState<boolean>(false);

  const [count, setCount] = useState(1);
  const [selectedOption, setSelectedOption] = useState<{
    id: number;
    name: string;
  } | null>(null);

  const [animate, setAnimate] = useState(false);

  const shouldDisable = useMemo(() => {
    return menu.isPreorder && !menu.isEnableButton;
  }, [menu]);

  const handleAnimation = useCallback(() => {
    setAnimate(true);
    setTimeout(() => setAnimate(false), 1000);
  }, []);

  const handleAddToCart = useCallback(
    (amount: number, optionId?: number, optionName?: string) => {
      if (amount === 0) return;

      if (!isMenuClicked || !menu.isFavorite) {
        handleAnimation();
      } else {
        const animationTrigger = document.getElementById(
          `menu-item-animation-trigger-${menu.id}`,
        );
        if (animationTrigger) animationTrigger.click();
      }

      onSelectMenu(amount, optionId, optionName);
    },
    [isMenuClicked, menu],
  );

  const handleClickMenuItem = useCallback(
    (isClickEvent?: boolean) => {
      if (disableClick) return;

      if (isClickEvent) setIsMenuClicked(true);

      if (noPopup) {
        const menuItemRecommendation = document.getElementById(
          `menu-item-recommendation-${menu.id}`,
        );
        if (menuItemRecommendation) menuItemRecommendation.click();
      } else {
        dispatch(
          appActions.showMenuDetailPopup.started({
            menu,
            isLimitedMenuSelected,
            onFavorite: (favorite) => {
              onFavorite(menu.code, favorite, () => {
                dispatch(appActions.setMenuDetailPopupFavorite(favorite));
              });
            },
            onSelectMenu: handleAddToCart,
            onClose: () => dispatch(appActions.closeMenuDetailPopup()),
          }),
        );
      }
    },
    [menu],
  );

  const isLimitedMenuSelected = useMemo(() => {
    if (!menu.isLimitedMenu) return false;

    return selectedLimitedMenus.some(
      (selectedMenu) => selectedMenu.code === menu.code,
    );
  }, [selectedLimitedMenus]);

  return (
    <>
      <div
        id={noPopup ? `menu-item-animation-trigger-${menu.id}` : undefined}
        style={{ display: "none" }}
        onClick={handleAnimation}
      ></div>

      <MenuItemRecommendationContainer>
        <MenuImage
          id={menu.id}
          imageUrl={menu.imageUrl}
          isFavorite={menu.isFavorite}
          onFavorite={(favorite) => onFavorite(menu.code, favorite)}
          onClickImage={() => handleClickMenuItem(noPopup)}
          width={110}
          height={110}
        />
        <MenuTextContent>
          <MenuNameRecommendationContainer>
            <MenuNameRecommendation>{menu.name}</MenuNameRecommendation>
          </MenuNameRecommendationContainer>

          <MenuPriceContainer>
            <MenuPrice
              price={menu.price}
              priceWithTax={menu.priceWithTax}
              isOriginalPrice={!!menu.campaign}
              size="md"
            />
          </MenuPriceContainer>

          {menu.campaign && (
            <>
              <MenuPriceContainer>
                <MenuPrice
                  price={menu.campaign.price}
                  priceWithTax={menu.campaign.priceWithTax}
                  isDiscountPrice
                  size="md"
                />
              </MenuPriceContainer>
              <MenuCampaignDetail>{menu.campaign.details}</MenuCampaignDetail>
            </>
          )}

          {/* Quantity selector and add to cart inside the card */}
          {showQuantitySelector && (
            <QuantitySection>
              <MenuCountSelect
                initialCount={quantity || (menu.isLimitedMenu ? 1 : 0)}
                onChange={(count) => onQuantityChange?.(count)}
                limited={menu.isLimitedMenu}
                limitedDisabled={isLimitedMenuSelected}
                shouldReset={shouldReset}
                disabled={shouldDisable}
                size="sm"
              />

              <Button
                disabled={!quantity || quantity === 0 || shouldDisable}
                text="カートに入れる"
                size="md"
                rounded="md"
                btnStyle="primary"
                onClick={() => onAddToCart?.()}
              />
            </QuantitySection>
          )}
        </MenuTextContent>
      </MenuItemRecommendationContainer>
    </>
  );
};

const MenuTextContent = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  margin-left: 16px;
  text-align: left;
`;

const MenuItemRecommendationContainer = styled.div`
  display: flex;
  flex-direction: Row;
  align-items: flex-start;
  text-align: left;
  margin: 0 0 16px 0;
  color: ${BASIC_DARK_GRAY};
  background-color: ${BASIC_WHITE};
  border-radius: 8px;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 12px;
    margin: 0 8px 16px 8px;
  }
`;

const MenuNameRecommendationContainer = styled.div`
  width: 100%;
  text-align: left;
`;

const MenuNameRecommendation = styled.div`
  font-size: 18px;
  font-weight: 600;
  text-align: left;
  margin: 8px 0 15px 0;
  cursor: pointer;
  line-height: 1.3;
`;

const MenuPriceContainer = styled.div`
  width: 100%;
  align-self: flex-start;
  text-align: left;
`;

const MenuCampaignDetail = styled.div`
  text-align: left;
  font-size: 14px;
  color: ${BASIC_DARK_GRAY};
  line-height: 1.3;
  margin-top: 4px;
  width: 100%;
  align-self: flex-start;
`;

const QuantitySection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 16px;

  @media (max-width: 768px) {
    justify-content: flex-start;
    gap: 12px;
    flex-wrap: wrap;
  }
`;
